// src/hooks/useVendor.js - Complete with Tracking Support
import { useState, useCallback } from 'react';
import { vendorApi } from '../api/vendorApi';

export const useVendor = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [analytics, setAnalytics] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await vendorApi.getStats();
      setStats(data?.stats || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch stats');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchProducts = useCallback(async (params) => {
    setLoading(true);
    setError(null);
    try {
      const data = await vendorApi.getProducts(params);
      setProducts(data?.products || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch products');
    } finally {
      setLoading(false);
    }
  }, []);

  const createProduct = useCallback(async (data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await vendorApi.createProduct(data);
      return response;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create product');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const updateProduct = useCallback(async (id, data) => {
    setLoading(true);
    setError(null);
    try {
      const response = await vendorApi.updateProduct(id, data);
      return response;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update product');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const deleteProduct = useCallback(async (id) => {
    setLoading(true);
    setError(null);
    try {
      await vendorApi.deleteProduct(id);
      return true;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete product');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchOrders = useCallback(async (params) => {
    setLoading(true);
    setError(null);
    try {
      const data = await vendorApi.getOrders(params);
      setOrders(data?.orders || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch orders');
    } finally {
      setLoading(false);
    }
  }, []);

  // ✅ FIX: Accept extraData and pass it to backend
  const updateOrderStatus = useCallback(async (id, status, extraData = {}) => {
    setLoading(true);
    setError(null);
    try {
      const response = await vendorApi.updateOrderStatus(id, status, extraData);
      return response;
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update order status');
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchCustomers = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await vendorApi.getCustomers();
      setCustomers(data?.customers || []);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch customers');
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchAnalytics = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await vendorApi.getAnalytics();
      setAnalytics(data?.analytics || null);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch analytics');
    } finally {
      setLoading(false);
    }
  }, []);

  const clearError = useCallback(() => {
    setError(null);
  }, []);

  return {
    loading,
    error,
    stats,
    products,
    orders,
    customers,
    analytics,
    fetchStats,
    fetchProducts,
    createProduct,
    updateProduct,
    deleteProduct,
    fetchOrders,
    updateOrderStatus,
    fetchCustomers,
    fetchAnalytics,
    clearError,
  };
};